"use client";

import React, { useEffect, useState } from "react";
import { hooks, apijs, logjs } from "@lib/client";
import DownloadDialog from "./DownloadDialog";

// ログ出力用のインスタンスを作成
const log = new logjs("useDownloadDialog");

// Download APIのインスタンスを作成
const api = new apijs("api/DownloadDialog");

// カスタムダイアログフックを取得
const { useCustomDialog } = hooks;

/**
 * ダウンロードダイアログのカスタムフック
 * 請求書・支払書のダウンロード機能を提供する
 * @param {Object} state - アプリケーションの状態
 * @param {Object} actions - アクション関数群（showLoading, hideLoading等）
 * @param {Object} params - パラメータ（請求書情報等）
 * @returns {Array} [formData, dialogControls] - フォームデータとダイアログ制御関数
 */
export const useDownloadDialog = (state, actions, params) => {
  // カスタムダイアログの制御関数を取得
  const { open, onClose, isOpen, renderDialog } = useCustomDialog(DownloadDialog);

  // パラメータから必要な情報を取得
  log.debug("useDownloadDialog params", params);
  log.debug("useDownloadDialog state", state);

  // 初期フォームデータ
  const initForm = {
    pageTitle: params?.pageTitle || "請求書",
    gyoshuFlg: state?.user?.Gyoshu_flg || null, // 1: 支払, 2: 請求
    isLoading: false,
    error: null,
    success: false,
  };
  const [downloadData, setDownloadData] = useState({ partsAry: {}, pageTitle: "請求書", gyoshuFlg: null });

  const [error, setError] = useState(null);
  // フォームの状態とプロパティを管理するフック
  const [form, formProps] = hooks.useFormEx(
    initForm,
    state, // アプリケーション状態
    actions // アクション関数群
  );

  // ダウンロード一覧の取得
  const init = async () => {
    formProps.setForm({ ...form, isLoading: true, error: null });
    actions.showLoading();
    try {
      // 実際のAPI呼び出し
      const res = await api.post({
        mode: "init"
      });

      if (res.result == true) {
        setDownloadData(res.data);
      } else {
        throw new Error(res.message || "ダウンロード一覧の取得に失敗しました");
      }
    } catch (error) {
      log.error("ダウンロード一覧の取得エラー:", error);
      setError(error.message);
    } finally {
      actions.hideLoading();
    }
  };

  // 文書のダウンロード
  const downloadDocument = async (kdate) => {
    if (!kdate) {
      setError("ダウンロード対象が指定されていません");
      return false;
    }

    actions.showLoading();

    try {
      // 実際のAPI呼び出し
      const response = await api.post({
        mode: "documentsPrint",
        wellsetDate: kdate,
      });

      if (res.result == true) {
        // ダウンロード処理
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `document_${kdate}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // ダウンロード履歴を更新
        await init();

        log.info("文書のダウンロード完了:", kdate);
        return true;
      } else {
        throw new Error(response.message || "文書のダウンロードに失敗しました");
      }
    } catch (error) {
      log.error("文書ダウンロードエラー:", error);
      setError(error.message);
      return false;
    } finally {
      actions.hideLoading();
    }
  };

  // 閲覧済みマークの更新
  const markAsViewed = async (kdate) => {
    if (!kdate) {
      return false;
    }
    actions.showLoading();
    try {
      const res = await api.post({
        mode: "markAsViewed",
        kdate: kdate,
        gyoshuFlg: state?.user?.Gyoshu_flg,
      });

      if (res.result == true) {
        // 一覧を再取得
        await init();
        log.info("閲覧済みマーク完了:", kdate);
        return true;
      } else {
        throw new Error(response.message || "閲覧済みマークに失敗しました");
      }
    } catch (error) {
      log.error("閲覧済みマークエラー:", error);
      setError(error.message);
      return false;
    }
  };

  // バリデーション
  const validateForm = (data) => {
    const errors = [];

    if (!data.gyoshuFlg) {
      errors.push("業種フラグが設定されていません");
    }

    return errors;
  };

  // フォームのリセット
  const resetForm = () => {
    setError(null);
    setDownloadData({});
  };

  // ダイアログを開く関数（パラメータ付き）
  /*
  const openDownloadDialog = (dialogParams = {}) => {
    const mergedParams = {
      ...params,
      ...dialogParams,
      // Download固有の関数を追加
      init,
      downloadDocument,
      markAsViewed,
      validateForm,
      resetForm,
    };
    open(mergedParams);
  };
  */

  // フックの戻り値：フォームデータとダイアログ制御関数
  return [
    {
      ...form,
      downloadData, // ダウンロードデータ
      error, // エラー
    }, // フォームの状態データ
    {
      ...formProps, // フォームのプロパティ（onChange等）
      downloadData, // ダウンロードデータ
      error, // エラー
      isOpen, // ダイアログの開閉状態
      renderDialog, // ダイアログのレンダリング関数
      onClose, // ダイアログを閉じる関数
      open, // ダイアログを開く関数 ← 追加
      // Download固有の関数
      init, // ダウンロード一覧取得
      downloadDocument, // 文書ダウンロード
      markAsViewed, // 閲覧済みマーク
      validateForm, // バリデーション
      resetForm, // フォームリセット
    },
  ];
};

export default useDownloadDialog;
